import { type Locale, Locales } from "@ai-admin/shared";
import { useTranslation } from "react-i18next";
import { Badge, Card, Chip, Input, Label, Textarea } from "../../ui/index.js";
import type { FieldValue } from "./sources.js";
import type { QuestionView } from "./types.js";

const LANG_LABELS: Record<Locale, string> = { "sr-Latn": "Srpski (latinica)", "sr-Cyrl": "Српски (ћирилица)", en: "English", ru: "Русский" };

/**
 * Обёртка вопроса у родителя: в широком контейнере (`@container` на карточке, от 32rem) подпись слева,
 * поле справа (поле возвращает две ячейки); в узком подпись над полем.
 */
export const questionRowClass = "@lg:grid @lg:grid-cols-[14rem_minmax(0,1fr)] @lg:items-start @lg:gap-x-6";

/**
 * Поле вопроса о правилах салона по его типу (`questions.ts`): текст, длинный текст, выбор, число, языки, список.
 * Одно и то же на экране черновика из источников и на шаге правил; `fromSources` добавляет пометку «из ваших источников».
 */
export function QuestionField({
  q,
  value,
  onChange,
  fromSources = false,
  idPrefix = "q",
}: {
  q: QuestionView;
  value: FieldValue | undefined;
  onChange: (v: FieldValue) => void;
  fromSources?: boolean;
  idPrefix?: string;
}) {
  const { t } = useTranslation();
  const id = `${idPrefix}-${q.id}`;
  const text = typeof value === "string" ? value : "";
  const label = (
    <div className="flex flex-wrap items-center gap-2 @lg:pt-3">
      <Label htmlFor={id} className="@lg:mb-0">
        {t(`onb.q.${q.id}`)}
      </Label>
      {fromSources && <Badge tone="blue">{t("onb.sources.fromSources")}</Badge>}
    </div>
  );
  const hint = <p className="mt-1 text-xs text-ink-3">{t(`onb.qh.${q.id}`)}</p>;
  // Две ячейки для `questionRowClass` родителя: на телефоне подпись над полем, на широком экране слева от него.
  const row = (field: React.ReactNode, withHint = true) => (
    <>
      {label}
      <div className="min-w-0">
        {field}
        {withHint && hint}
      </div>
    </>
  );

  if (q.kind === "languages") {
    const langs = Array.isArray(value) ? value : [];
    return row(
      <div className="flex flex-wrap gap-2 @lg:pt-1.5">
        {Locales.map((l) => (
          <Chip key={l} active={langs.includes(l)} onClick={() => onChange(langs.includes(l) ? langs.filter((x) => x !== l) : [...langs, l])}>
            {LANG_LABELS[l]}
          </Chip>
        ))}
      </div>,
    );
  }
  if (q.id === "autonomy") {
    return row(
      <div className="space-y-2">
        {q.options.map((a) => (
          <button key={a} type="button" onClick={() => onChange(a)} className="block w-full text-left">
            <Card selected={text === a} className={text === a ? "" : "hover:border-line-strong"}>
              <div className="font-medium">{t(`onb.opt.autonomy.${a}`)}</div>
            </Card>
          </button>
        ))}
      </div>,
      false,
    );
  }
  if (q.kind === "choice") {
    return row(
      <div className="flex flex-wrap gap-2 @lg:pt-1.5">
        {q.options.map((o) => (
          <Chip key={o} active={text === o} onClick={() => onChange(text === o ? "" : o)}>
            {t(`onb.opt.${q.id}.${o}`)}
          </Chip>
        ))}
      </div>,
      false,
    );
  }
  if (q.kind === "number") {
    return row(<Input id={id} inputMode="numeric" value={text} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} />);
  }
  const max = q.target === "profile" ? 300 : 2000;
  return row(
    q.kind === "longtext" || q.kind === "list" ? (
      <Textarea id={id} rows={3} value={text} onChange={(e) => onChange(e.target.value)} maxLength={q.kind === "list" ? undefined : max} />
    ) : (
      <Input id={id} value={text} onChange={(e) => onChange(e.target.value)} maxLength={max} />
    ),
  );
}
